/**
 * ¿Puede ARIA aceptar este fichero, y si no, por qué?
 *
 * Existe porque el filtro que corría antes del ruteo **anulaba** los tres
 * predicados que sí saben mirar la extensión. `isTableFile`, `isWordFile` e
 * `isAcceptedOcrFile` contemplan el MIME vacío —el navegador no siempre lo
 * reporta para `.csv`, `.xls` o `.heic`— pero el consumidor filtraba antes con
 * `ACCEPT_DOC_TYPES.includes(f.type)`, así que un `.csv` arrastrado se rechazaba
 * como "formato no admitido" sin llegar nunca a ellos.
 *
 * Aquí se decide con los MISMOS predicados con los que luego se rutea
 * (`handleAddDocuments`), de modo que filtro y ruteo no pueden divergir: si algo
 * se acepta, hay un canal que lo recoge.
 *
 * ## Formato y tamaño son dos cosas
 *
 * `isAcceptedOcrFile` los mezcla en un booleano, y por eso hoy un PDF de 12 MB
 * desaparece sin un solo aviso: pasa el filtro de MIME, se enruta a OCR y ahí se
 * cae en silencio. `describeFile` los separa para poder decir "formato no
 * admitido" o "supera 10 MB", que son dos arreglos distintos para el usuario.
 */

import { isTableFile, TABLE_ACCEPTED_TYPES, TABLE_MAX_BYTES } from './uploadTableFile';
import { isWordFile, WORD_ACCEPTED_TYPES } from './extractWordText';
import { isAcceptedOcrFile, OCR_ACCEPTED_TYPES, OCR_MAX_BYTES } from './uploadOcrDocument';

/**
 * Extensiones de todo lo que ARIA sabe leer.
 *
 * Va al `accept` del `<input type="file">` JUNTO a los MIME, no en su lugar: con
 * solo MIME, el diálogo del sistema **oculta** los ficheros que el SO no sabe
 * etiquetar —el mismo `.csv` que el filtro rechazaba—, así que el usuario ni
 * siquiera podía elegirlo. Mismo patrón que `IMPORT_ACCEPT` en
 * `lib/imports/parseSpreadsheet.js`, que ya mezcla las dos formas.
 */
export const ARIA_ACCEPTED_EXTENSIONS = [
  '.xlsx', '.xls', '.csv',           // tabular  → importar_tabla
  '.docx',                            // Word     → texto inline
  '.pdf', '.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic', '.heif', // OCR → Bandeja
];

/** MIME + extensiones, listo para el atributo `accept`. */
export const ARIA_ACCEPT_ATTR = [
  ...OCR_ACCEPTED_TYPES,
  ...TABLE_ACCEPTED_TYPES,
  ...WORD_ACCEPTED_TYPES,
  ...ARIA_ACCEPTED_EXTENSIONS,
].join(',');

/** El canal que recogería el fichero, o `null` si ninguno sabe leerlo. */
export function channelForFile(file) {
  if (!file) return null;
  if (isTableFile(file)) return 'table';
  if (isWordFile(file)) return 'word';
  // `isAcceptedOcrFile` mezcla formato y tamaño; aquí solo interesa el formato,
  // el tamaño lo reporta `describeFile` por separado.
  if (OCR_ACCEPTED_TYPES.includes(file.type) || /\.(pdf|jpe?g|png|webp|gif|hei[cf])$/i.test(file.name || '')) return 'ocr';
  return null;
}

/**
 * Veredicto por fichero: `{ ok, channel, reason }`.
 *
 * `reason` distingue `'format'` de `'size'` para que el consumidor pueda decir
 * qué hacer al respecto — cambiar de fichero no es lo mismo que comprimirlo.
 */
export function describeFile(file) {
  const channel = channelForFile(file);
  if (!channel) return { ok: false, channel: null, reason: 'format' };

  const maxBytes = channel === 'table' ? TABLE_MAX_BYTES : channel === 'ocr' ? OCR_MAX_BYTES : null;
  if (maxBytes && file.size > maxBytes) {
    return { ok: false, channel, reason: 'size', maxBytes };
  }
  // Word no tiene tope declarado en ningún sitio del repo; no se inventa uno aquí.
  return { ok: true, channel, reason: null };
}

/** `true` si ARIA puede hacer algo con el fichero. Atajo de `describeFile`. */
export function isAcceptedAriaFile(file) {
  return describeFile(file).ok;
}

/**
 * Parte una lista en aceptados y rechazados, conservando el motivo de cada
 * rechazo. Es lo que permite avisar POR FICHERO en vez de tragárselos en bloque.
 */
export function partitionFiles(files) {
  const accepted = [];
  const rejected = [];
  for (const file of Array.from(files || [])) {
    const verdict = describeFile(file);
    if (verdict.ok) accepted.push(file);
    else rejected.push({ file, ...verdict });
  }
  return { accepted, rejected };
}

export { isAcceptedOcrFile, OCR_MAX_BYTES, TABLE_MAX_BYTES };
